import { pluralize } from 'utils/misc'
import { parseVisibilityTime } from 'utils/time'
import { fromUTCMidnightSeconds } from 'utils/time'
import { DATE_FORMAT_MAP, DEPLOYMENT_KEYS, DEPLOYMENT_VALUES } from 'utils/constants'
import { getChainDeploymentStudyScheduleIds } from 'utils/chainDeploymentUtils'

/**
 * Normalizes metadata.schedule to always return an array.
 * Supports legacy single schedule objects and the multi-schedule array shape.
 */
export const normalizeSchedules = schedule => {
  if (!schedule) return []
  if (Array.isArray(schedule)) return schedule.filter(Boolean)
  if (typeof schedule === 'object') return [schedule]
  return []
}

export const getScheduleStartTypeLabel = schedule => {
  const { deploy = {}, chain_deployment_info: chainDeploymentInfo = {} } = schedule || {}

  if ('now' in deploy) return 'Immediately'
  if ('absolute' in deploy) return 'Exact Date'
  if (DEPLOYMENT_KEYS.firstLogin in deploy) return 'Relative to Participant Start'
  if (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeploymentInfo) return 'Triggered by Visit'
  if (
    DEPLOYMENT_KEYS.chainDeployOffset in deploy ||
    Array.isArray(chainDeploymentInfo.prev_instrument_id)
  ) {
    return 'Triggered by Instrument'
  }
  return 'Immediately'
}

/**
 * Build id → title map from instruments list (formatted rows or raw instruments).
 */
export const buildInstrumentNameMap = (instruments = []) =>
  (instruments || []).reduce((acc, item) => {
    if (!item) return acc
    const id = item.id
    if (!id) return acc

    if (item.payload?.title) {
      acc[id] = item.payload.title
      return acc
    }
    if (item.title) {
      acc[id] = item.title
      return acc
    }
    if (Array.isArray(item)) {
      const nameCell = item.find(cell => cell?.key === 'name')
      if (nameCell?.value) acc[id] = nameCell.value
    }
    return acc
  }, {})

const getVisitScheduleName = ({ visitId, visitScheduleId, visitTemplates = [] }) => {
  const currentVisitSchedule =
    visitTemplates.find(template => template.id === visitScheduleId) || {}
  const visit = currentVisitSchedule?.visits?.find(item => item.id === visitId)
  return {
    scheduleName: currentVisitSchedule?.name,
    visitName: visit?.name,
  }
}

/**
 * Dummy / non-cycled cycles are visit placeholders (cycle absent).
 * Matches InstrumentVisitSelection so Start labels stay consistent with the editor tags.
 */
const isNonCycledCycle = cycle => {
  if (cycle?.is_dummy || cycle?.isDummy || cycle?.metadata?.is_dummy) return true
  const visits = cycle?.visits || []
  return visits.length === 1 && !!cycle?.name && visits[0]?.name === cycle.name
}

const flattenArmCycles = (arms = []) =>
  (arms || []).reduce((acc, arm) => {
    const armCycles =
      arm?.phases?.reduce((phaseAcc, phase) => [...phaseAcc, ...(phase?.cycles || [])], []) || []
    return [
      ...acc,
      ...armCycles.map(cycle => ({
        ...cycle,
        armId: arm?.id,
        armName: arm?.name,
      })),
    ]
  }, [])

const findArmForVisit = (studyVisitSchedule = [], visitScheduleId, visitId) => {
  if (!visitScheduleId && !visitId) return null

  for (const arm of studyVisitSchedule) {
    for (const phase of arm?.phases || []) {
      const cycles = phase?.cycles || []
      if (visitScheduleId && cycles.some(cycle => cycle.id === visitScheduleId)) return arm
      const templates = phase?.visit_templates || []
      if (visitScheduleId && templates.some(template => template.id === visitScheduleId)) return arm
    }
  }

  if (!visitId) return null

  for (const arm of studyVisitSchedule) {
    for (const phase of arm?.phases || []) {
      const hasVisitInCycle = (phase?.cycles || []).some(cycle =>
        cycle.visits?.some(visit => visit.id === visitId),
      )
      if (hasVisitInCycle) return arm
      const hasVisitInTemplate = (phase?.visit_templates || []).some(template =>
        template.visits?.some(visit => visit.id === visitId),
      )
      if (hasVisitInTemplate) return arm
    }
  }

  return null
}

const formatTriggeredVisitLabel = ({
  armName,
  cycleName,
  visitName,
  isDummyCycle,
  isArmsSupported,
}) => {
  if (!visitName) return null
  if (isArmsSupported && armName) {
    if (!isDummyCycle && cycleName) return `${armName}: ${cycleName}: ${visitName}`
    return `${armName}: ${visitName}`
  }
  if (!isDummyCycle && cycleName) return `${cycleName}: ${visitName}`
  return visitName
}

/**
 * Resolve Triggered-by-Visit labels:
 * - cycle absent / dummy: "Arm: Visit"
 * - arm + cycle + visit: "Arm: Cycle: Visit"
 * Arms with no selected visit are omitted.
 * GOLD-10125 AC 23.1
 */
export const getTriggeredVisitLabels = (
  schedule,
  {
    visitTemplates = [],
    studyVisitSchedule = [],
    isVisitCycleSupported = false,
    isArmsSupported = false,
  } = {},
) => {
  const chainDeploymentInfo = schedule?.chain_deployment_info || {}
  if (!(DEPLOYMENT_KEYS.chainDeployByVisit in chainDeploymentInfo)) return []

  const {
    visit_id: visitId,
    visit_ids: visitIds = [],
    visit_schedule_id: visitScheduleId,
    visit_schedule_ids: visitScheduleIds = [],
  } = chainDeploymentInfo

  const armIds = getChainDeploymentStudyScheduleIds(chainDeploymentInfo)
  const selectedArms = armIds.length
    ? armIds.map(id => studyVisitSchedule.find(arm => arm.id === id)).filter(Boolean)
    : studyVisitSchedule
  const cycles = isVisitCycleSupported ? flattenArmCycles(selectedArms) : []

  const deployVisits =
    Array.isArray(visitIds) && visitIds.length > 0
      ? visitIds.map((id, index) => ({
          visit_id: id,
          visit_schedule_id: visitScheduleIds[index] || visitScheduleIds[0] || visitScheduleId || '',
        }))
      : visitId
        ? [{ visit_id: visitId, visit_schedule_id: visitScheduleId || '' }]
        : []

  const labels = deployVisits
    .map(({ visit_id: vid, visit_schedule_id: vsid }) => {
      if (isVisitCycleSupported) {
        const cycle =
          cycles.find(item => item.id === vsid && item.visits?.some(visit => visit.id === vid)) ||
          cycles.find(item => item.id === vsid) ||
          cycles.find(item => item.visits?.some(visit => visit.id === vid))
        const cycleVisit = cycle?.visits?.find(item => item.id === vid)
        if (cycle && cycleVisit) {
          return formatTriggeredVisitLabel({
            armName: cycle.armName,
            cycleName: cycle.name,
            visitName: cycleVisit.name,
            isDummyCycle: isNonCycledCycle(cycle),
            isArmsSupported,
          })
        }
      }

      const { scheduleName, visitName } = getVisitScheduleName({
        visitId: vid,
        visitScheduleId: vsid,
        visitTemplates,
      })
      if (!visitName) return null

      const matchingArm = findArmForVisit(studyVisitSchedule, vsid, vid)
      const armName =
        matchingArm?.name ||
        studyVisitSchedule?.find(arm => arm.id === chainDeploymentInfo.study_schedule_id)?.name ||
        ''

      if (isArmsSupported && armName) return `${armName}: ${visitName}`
      if (scheduleName) return `${scheduleName}: ${visitName}`
      return visitName
    })
    .filter(Boolean)

  return labels
}

export const getScheduleStartValue = (
  schedule,
  articleType,
  deployOnConfirm,
  {
    instrumentNameMap = {},
    visitTemplates = [],
    studyVisitSchedule = [],
    isVisitCycleSupported = false,
    isArmsSupported = false,
  } = {},
) => {
  const { deploy = {}, chain_deployment_info: chainDeploymentInfo = {} } = schedule || {}
  const { prev_instrument_id: prevInstrumentId } = chainDeploymentInfo

  if ('now' in deploy) return 'Immediately'

  if ('absolute' in deploy) {
    return `Exact Date ${fromUTCMidnightSeconds(deploy.absolute).format(DATE_FORMAT_MAP.main)}`
  }

  if (Array.isArray(prevInstrumentId) && !(DEPLOYMENT_KEYS.chainDeployByVisit in chainDeploymentInfo)) {
    const names = prevInstrumentId.map(id => instrumentNameMap[id]).filter(Boolean)
    if (names.length) return names.join(', ')
    return 'Triggered by Instrument'
  }

  if (DEPLOYMENT_KEYS.chainDeployOffset in deploy) {
    const scheduledTime = deploy.chain_deploy_offset
      ? pluralize(deploy.chain_deploy_offset, 'day', 'days')
      : 'Immediately'
    return `${scheduledTime} after first instrument completes`
  }

  if (DEPLOYMENT_KEYS.chainDeployByVisit in chainDeploymentInfo) {
    const visitLabels = getTriggeredVisitLabels(schedule, {
      visitTemplates,
      studyVisitSchedule,
      isVisitCycleSupported,
      isArmsSupported,
    })
    if (visitLabels.length) return visitLabels.join(', ')

    // Fallback when visit names cannot be resolved yet
    const isMissedArticleType = articleType === 'missed_visit_follow_up'
    const visitType = isMissedArticleType ? 'missed visit' : `${deployOnConfirm ? 'confirmed ' : ''}visit`
    const { days_offset: daysOffset } = deploy
    if (daysOffset > 0) return `${pluralize(daysOffset, 'day', 'days')} after ${visitType}`
    if (daysOffset < 0) return `${pluralize(Math.abs(daysOffset), 'day', 'days')} before ${visitType}`
    return `On day of ${visitType}`
  }

  if (deploy.first_login) {
    return `Relative ${pluralize(deploy.first_login.interval, 'Day', 'Days')} after First Login`
  }

  return 'Immediately'
}

export const getScheduleExpireValue = schedule => {
  const { recurring, expire = {}, deploy = {} } = schedule || {}

  if ('never' in expire && (!recurring || !recurring.recurrences)) {
    // GOLD-10125: "Never Expire" option displays as "When Complete"
    return 'When Complete'
  }
  if (recurring && recurring.recurrences) {
    return `After ${recurring.recurrences} occurrences`
  }
  if ('first_login' in expire && deploy.first_login) {
    const offset = expire.first_login.interval - deploy.first_login.interval
    return offset === 0 ? 'Same day as instrument starts' : `${offset} days after instrument starts`
  }
  if ('chain_expire_offset' in expire && deploy.chain_deploy_offset != null) {
    return `${pluralize(
      expire.chain_expire_offset - deploy.chain_deploy_offset,
      'day',
      'days',
    )} after instrument starts`
  }
  if ('expire_type' in expire) {
    const { expire_type: expireType, days_offset: daysOffset } = expire
    if (expireType === DEPLOYMENT_VALUES.completionOfVisit) {
      if (!daysOffset) return 'On day visit is completed'
      return `${pluralize(daysOffset, 'day', 'days')} after completion of visit`
    }
    if (expireType === DEPLOYMENT_VALUES.receiptOfArticle) {
      return `${pluralize(daysOffset, 'day', 'days')} after receipt of item`
    }
    return 'When Complete'
  }
  if ('relative_to_send_ideal' in expire) {
    const { relative_to_send_ideal: relativeToSendIdeal } = expire
    return relativeToSendIdeal?.interval === 1
      ? 'Same day as instrument starts'
      : `${pluralize(relativeToSendIdeal?.interval - 1, 'day', 'days')} after instrument starts`
  }
  if (expire.absolute != null) {
    return fromUTCMidnightSeconds(expire.absolute).format(DATE_FORMAT_MAP.main)
  }
  return 'When Complete'
}

export const getScheduleFrequencyValue = schedule => {
  const { recurring } = schedule || {}
  if (!recurring) return 'No Repeat'
  if (recurring.scheme === 'n-days') return `Repeat after every ${pluralize(recurring.interval, 'day', 'days')}`
  if (recurring.scheme === 'n-months') {
    return `Repeat after every ${pluralize(recurring.interval, 'month', 'months')}`
  }
  if (recurring.scheme === 'n-hours') return `Repeat after every ${pluralize(recurring.interval, 'hour', 'hours')}`
  if (recurring.scheme === 'n-minutes') {
    return `Repeat after every ${pluralize(recurring.interval, 'minute', 'minutes')}`
  }
  return 'Repeat Immediately'
}

const formatVisibilityRangeLabel = (start, end) => {
  const startMoment = parseVisibilityTime(start)
  const endMoment = parseVisibilityTime(end)
  if (!startMoment || !endMoment) return null
  return `${startMoment.format('h:mm A')} - ${endMoment.format('h:mm A')}`
}

/**
 * GOLD-12204: Resolve visibility from schedule first (per-schedule multi-schedule),
 * then fall back to instrument metadata for legacy single-schedule instruments.
 */
export const getScheduleVisibilityValue = (schedule, metadata = null) => {
  const visibility = schedule?.visibility ?? metadata?.visibility
  const visibilityRanges = schedule?.visibilityRanges ?? metadata?.visibilityRanges
  const { recurring } = schedule || {}

  let visibilityText
  if (visibility === 'specific_time') {
    const rangeLabels = (visibilityRanges || [])
      .map(range => formatVisibilityRangeLabel(range?.start, range?.end))
      .filter(Boolean)
    visibilityText = rangeLabels.length > 0 ? rangeLabels.join(', ') : 'At Specific Times'
  } else {
    visibilityText = visibility === 'all_day' || !visibility ? 'All day' : 'At Specific Times'
  }

  if (recurring && recurring.interval !== recurring.visible_days) {
    return `${visibilityText}, ${recurring.visible_days} day(s)`
  }
  return `${visibilityText}, Everyday`
}

const getAudienceLabel = type => {
  if (type === 'all_caregivers') return 'Caregivers'
  if (type === 'all_users') return 'Participants & Caregivers'
  if (type === 'all_participants') return 'All Participants'
  return 'All Participants'
}

const generateSitesMap = siteSelectionList =>
  (siteSelectionList || []).reduce((acc, item) => {
    acc[item[0]?.value] = item[1]?.value
    return acc
  }, {})

/**
 * GOLD-10125 Send to formats:
 * - All Participants
 * - Age Range: 20-50
 * - Track (1): Track 2
 * - Site (1): Site LA
 * - Site (1): Site LA, Track (1): Track 2, Age Range: 20-50
 */
export const getScheduleSendToValue = (schedule, cohortMap = {}, siteSelectionList = []) => {
  const { cohort } = schedule || {}
  if (!cohort) return 'All Participants'

  const cohorts = Array.isArray(cohort) ? cohort : [cohort]
  const detailLabels = []

  cohorts.forEach(item => {
    const { filter = {} } = item || {}

    if (filter.min_age !== undefined && filter.max_age !== undefined) {
      detailLabels.push(`Age Range: ${filter.min_age}-${filter.max_age}`)
    }
    if (Array.isArray(filter.include) && filter.include.length > 0) {
      const trackNames = filter.include.map(id => cohortMap[id]).filter(Boolean)
      if (trackNames.length) {
        detailLabels.push(`Track (${trackNames.length}): ${trackNames.join(', ')}`)
      }
    }
    if (Array.isArray(filter.site_ids) && filter.site_ids.length > 0) {
      const siteMap = generateSitesMap(siteSelectionList)
      const siteNames = filter.site_ids.map(id => siteMap[id]).filter(Boolean)
      if (siteNames.length) {
        detailLabels.push(`Site (${siteNames.length}): ${siteNames.join(', ')}`)
      }
    }
  })

  if (detailLabels.length) return detailLabels.join(', ')

  const primaryType = cohorts[0]?.type
  return getAudienceLabel(primaryType)
}

export const hasConditionalDeployment = schedule => {
  const schedules = normalizeSchedules(schedule)
  return schedules.some(item => Array.isArray(item?.chain_deployment_info?.prev_instrument_id))
}
